import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import AdminLayout from '../../components/admin/AdminLayout';
import { productsApi, categoriesApi } from '../../services/api';
import { Plus, Edit, Trash2, Search, Check, X, Eye, Settings } from 'lucide-react';

const AdminProducts = () => {
  const navigate = useNavigate();
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({});

  useEffect(() => {
    fetchProducts();
    fetchCategories();
  }, []);

  const fetchCategories = async () => {
    try {
      // Fetch all categories (flat list including subcategories) for admin panel
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/categories/all/flat`);
      const data = await response.json();
      setCategories(data);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  // Organize categories hierarchically for display
  const organizeCategories = () => {
    const organized = [];
    
    // First add parent categories
    const parents = categories.filter(c => !c.parent_id);
    
    parents.forEach(parent => {
      organized.push({
        ...parent,
        displayName: parent.name,
        level: 0
      });
      
      // Then add children
      const children = categories.filter(c => c.parent_id === parent.id);
      children.forEach(child => {
        organized.push({
          ...child,
          displayName: `  └─ ${child.name}`,
          level: 1
        });
      });
    });
    
    return organized;
  };

  const fetchProducts = async () => {
    try {
      const data = await productsApi.getAll();
      console.log('Admin Products loaded:', data.length);
      setProducts(data);
    } catch (error) {
      console.error('Error loading products:', error);
      if (error.response?.status === 401) {
        navigate('/admin/login');
      } else {
        alert('Ürünler yüklenirken hata oluştu: ' + (error.message || 'Bilinmeyen hata'));
      }
    } finally {
      setLoading(false);
    }
  };

  const startEditing = (product) => {
    setEditingId(product.id);
    setEditForm({
      name: product.name,
      category: product.category,
      price: product.old_price || product.price,
      discount: product.discount || 0,
      stock: product.stock,
      featured: product.featured || false,
      bestseller: product.bestseller || false,
      new: product.new || false,
      weekly_deal: product.weekly_deal || false
    });
  };

  const cancelEditing = () => {
    setEditingId(null);
    setEditForm({});
  };

  const saveEditing = async () => {
    try {
      const basePrice = parseFloat(editForm.price);
      const discountPercent = parseFloat(editForm.discount) || 0;
      
      // İndirimli fiyat hesapla
      const discountedPrice = discountPercent > 0 
        ? Math.round(basePrice * (1 - discountPercent / 100))
        : basePrice;

      const updatedProduct = {
        ...products.find(p => p.id === editingId),
        name: editForm.name,
        category: editForm.category,
        price: discountedPrice,
        old_price: discountPercent > 0 ? basePrice : 0,
        discount: discountPercent,
        stock: parseInt(editForm.stock),
        featured: editForm.featured,
        bestseller: editForm.bestseller,
        new: editForm.new,
        weekly_deal: editForm.weekly_deal
      };

      await productsApi.update(editingId, updatedProduct);
      
      setProducts(products.map(p => 
        p.id === editingId ? updatedProduct : p
      ));
      
      setEditingId(null);
      setEditForm({});
      alert('Ürün başarıyla güncellendi!');
    } catch (error) {
      console.error('Error updating product:', error);
      alert('Ürün güncellenirken hata oluştu!');
    }
  };

  const handleEditFormChange = (field, value) => {
    setEditForm(prev => ({ ...prev, [field]: value }));
  };

  const calculateDiscountedPrice = (price, discount) => {
    if (!discount || discount === 0) return price;
    return Math.round(price * (1 - discount / 100));
  };

  const handleDelete = async (id) => {
    if (window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) {
      try {
        await productsApi.delete(id);
        setProducts(products.filter(p => p.id !== id));
        alert('Ürün başarıyla silindi!');
      } catch (error) {
        alert('Ürün silinirken hata oluştu!');
      }
    }
  };

  const filteredProducts = products.filter(p =>
    p.name.toLowerCase().includes(searchQuery.toLowerCase())
  );

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-red-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div>
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-4 md:mb-0">Ürün Yönetimi</h1>
          <Link
            to="/admin/products/add"
            className="inline-flex items-center gap-2 bg-red-600 text-white px-6 py-3 rounded-lg hover:bg-red-700 transition-colors font-semibold"
          >
            <Plus size={20} />
            Yeni Ürün Ekle
          </Link>
        </div>

        {/* Search */}
        <div className="bg-white rounded-lg shadow-md p-4 mb-6">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <input
              type="text"
              placeholder="Ürün ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-red-600"
            />
          </div>
        </div>

        {/* Products Table */}
        <div className="bg-white rounded-lg shadow-md overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">ID</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Görsel</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Ürün Adı</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Kategori</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Fiyat & İndirim</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Stok</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">Özel Etiketler</th>
                  <th className="text-left py-3 px-4 font-semibold text-gray-700">İşlemler</th>
                </tr>
              </thead>
              <tbody>
                {filteredProducts.map((product) => {
                  const isEditing = editingId === product.id;
                  
                  return (
                    <tr key={product.id} className={`border-b ${isEditing ? 'bg-blue-50' : 'hover:bg-gray-50'}`}>
                      {/* Product ID */}
                      <td className="py-3 px-4">
                        <span className="font-mono text-sm font-semibold text-gray-600 bg-gray-100 px-2 py-1 rounded" data-testid={`product-id-${product.id}`}>
                          #{product.id}
                        </span>
                      </td>

                      {/* Image */}
                      <td className="py-3 px-4">
                        <img
                          src={product.image}
                          alt={product.name}
                          className="w-16 h-16 object-cover rounded border-2 border-gray-300 shadow-sm"
                        />
                      </td>

                      {/* Product Name */}
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editForm.name}
                            onChange={(e) => handleEditFormChange('name', e.target.value)}
                            className="w-full px-2 py-1 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        ) : (
                          <div className="max-w-md">
                            <p className="font-medium text-gray-800 line-clamp-2 text-sm">{product.name}</p>
                          </div>
                        )}
                      </td>

                      {/* Category */}
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <div className="space-y-1 max-h-48 overflow-y-auto border border-blue-400 rounded p-2 bg-white">
                            {organizeCategories().map(cat => (
                              <label 
                                key={cat.id} 
                                className="flex items-center gap-2 p-1 hover:bg-blue-50 rounded cursor-pointer"
                              >
                                <input
                                  type="checkbox"
                                  checked={Array.isArray(editForm.category) ? editForm.category.includes(cat.slug) : editForm.category === cat.slug}
                                  onChange={(e) => {
                                    const currentCategories = Array.isArray(editForm.category) ? editForm.category : [editForm.category];
                                    if (e.target.checked) {
                                      handleEditFormChange('category', [...currentCategories, cat.slug]);
                                    } else {
                                      handleEditFormChange('category', currentCategories.filter(c => c !== cat.slug));
                                    }
                                  }}
                                  className="rounded text-blue-600 focus:ring-blue-500"
                                />
                                <span className="text-sm" style={{ fontFamily: 'monospace' }}>
                                  {cat.displayName}
                                </span>
                              </label>
                            ))}
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {(Array.isArray(product.category) ? product.category : [product.category]).map((cat, idx) => (
                              <span key={idx} className="inline-block bg-gray-100 text-gray-700 px-2 py-1 rounded-full text-xs">
                                {cat}
                              </span>
                            ))}
                          </div>
                        )}
                      </td>

                      {/* Price & Discount */}
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <div className="space-y-1">
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={editForm.price}
                                onChange={(e) => handleEditFormChange('price', e.target.value)}
                                placeholder="Asıl Fiyat"
                                className="w-20 px-2 py-1 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                              <span className="text-xs text-gray-600">TL</span>
                            </div>
                            <div className="flex items-center gap-1">
                              <input
                                type="number"
                                value={editForm.discount}
                                onChange={(e) => handleEditFormChange('discount', e.target.value)}
                                placeholder="%"
                                min="0"
                                max="100"
                                className="w-16 px-2 py-1 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                              />
                              <span className="text-xs text-gray-600">% İnd.</span>
                            </div>
                            {editForm.discount > 0 && (
                              <div className="text-xs text-green-600 font-semibold">
                                = {calculateDiscountedPrice(editForm.price, editForm.discount)} TL
                              </div>
                            )}
                          </div>
                        ) : (
                          <div>
                            <div className="text-red-600 font-bold text-base">
                              {product.price.toLocaleString('tr-TR')} TL
                            </div>
                            {product.discount > 0 && product.old_price && (
                              <>
                                <div className="text-gray-400 line-through text-xs">
                                  {product.old_price.toLocaleString('tr-TR')} TL
                                </div>
                                <div className="text-green-600 text-xs font-medium">
                                  -%{product.discount}
                                </div>
                              </>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Stock */}
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <input
                            type="number"
                            value={editForm.stock}
                            onChange={(e) => handleEditFormChange('stock', e.target.value)}
                            className="w-16 px-2 py-1 border border-blue-400 rounded focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                          />
                        ) : (
                          <span className={`inline-block px-2 py-1 rounded-full text-xs font-semibold ${
                            product.stock > 10 ? 'bg-green-100 text-green-700' : 
                            product.stock > 0 ? 'bg-yellow-100 text-yellow-700' : 
                            'bg-red-100 text-red-700'
                          }`}>
                            {product.stock}
                          </span>
                        )}
                      </td>

                      {/* Special Tags */}
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <div className="space-y-1">
                            <label className="flex items-center gap-1 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editForm.featured}
                                onChange={(e) => handleEditFormChange('featured', e.target.checked)}
                                className="rounded text-blue-600 focus:ring-2 focus:ring-blue-500"
                              />
                              <span className="text-gray-700">Öne Çıkan</span>
                            </label>
                            <label className="flex items-center gap-1 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editForm.bestseller}
                                onChange={(e) => handleEditFormChange('bestseller', e.target.checked)}
                                className="rounded text-green-600 focus:ring-2 focus:ring-green-500"
                              />
                              <span className="text-gray-700">Çok Satan</span>
                            </label>
                            <label className="flex items-center gap-1 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editForm.new}
                                onChange={(e) => handleEditFormChange('new', e.target.checked)}
                                className="rounded text-purple-600 focus:ring-2 focus:ring-purple-500"
                              />
                              <span className="text-gray-700">Yeni</span>
                            </label>
                            <label className="flex items-center gap-1 text-xs cursor-pointer">
                              <input
                                type="checkbox"
                                checked={editForm.weekly_deal}
                                onChange={(e) => handleEditFormChange('weekly_deal', e.target.checked)}
                                className="rounded text-orange-600 focus:ring-2 focus:ring-orange-500"
                              />
                              <span className="text-gray-700">Haftanın Fırsatı</span>
                            </label>
                          </div>
                        ) : (
                          <div className="flex flex-wrap gap-1">
                            {product.featured && (
                              <span className="inline-block bg-blue-100 text-blue-700 px-2 py-0.5 rounded text-xs font-medium">
                                Öne Çıkan
                              </span>
                            )}
                            {product.bestseller && (
                              <span className="inline-block bg-green-100 text-green-700 px-2 py-0.5 rounded text-xs font-medium">
                                Çok Satan
                              </span>
                            )}
                            {product.new && (
                              <span className="inline-block bg-purple-100 text-purple-700 px-2 py-0.5 rounded text-xs font-medium">
                                Yeni
                              </span>
                            )}
                            {product.weekly_deal && (
                              <span className="inline-block bg-orange-100 text-orange-700 px-2 py-0.5 rounded text-xs font-medium">
                                Haftanın Fırsatı
                              </span>
                            )}
                            {!product.featured && !product.bestseller && !product.new && !product.weekly_deal && (
                              <span className="text-gray-400 text-xs">-</span>
                            )}
                          </div>
                        )}
                      </td>

                      {/* Actions */}
                      <td className="py-3 px-4">
                        {isEditing ? (
                          <div className="flex gap-1">
                            <button
                              onClick={saveEditing}
                              className="p-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                              title="Kaydet"
                            >
                              <Check size={16} />
                            </button>
                            <button
                              onClick={cancelEditing}
                              className="p-2 bg-gray-600 text-white rounded hover:bg-gray-700 transition-colors"
                              title="İptal"
                            >
                              <X size={16} />
                            </button>
                          </div>
                        ) : (
                          <div className="flex gap-1">
                            <Link
                              to={`/product/${product.id}`}
                              target="_blank"
                              className="p-2 bg-green-600 text-white rounded hover:bg-green-700 transition-colors"
                              title="Ürün Detayına Git"
                            >
                              <Eye size={16} />
                            </Link>
                            <button
                              onClick={() => startEditing(product)}
                              className="p-2 bg-blue-600 text-white rounded hover:bg-blue-700 transition-colors"
                              title="Hızlı Düzenle"
                            >
                              <Edit size={16} />
                            </button>
                            <Link
                              to={`/admin/products/edit/${product.id}`}
                              className="p-2 bg-purple-600 text-white rounded hover:bg-purple-700 transition-colors"
                              title="Detaylı Düzenle"
                            >
                              <Settings size={16} />
                            </Link>
                            <button
                              onClick={() => handleDelete(product.id)}
                              className="p-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
                              title="Sil"
                            >
                              <Trash2 size={16} />
                            </button>
                          </div>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {filteredProducts.length === 0 && (
            <div className="text-center py-12 text-gray-500">
              Ürün bulunamadı
            </div>
          )}
        </div>

        <div className="mt-4 text-sm text-gray-600">
          Toplam {filteredProducts.length} ürün
        </div>
      </div>
    </AdminLayout>
  );
};

export default AdminProducts;
